import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
	Splitter,
	Typography,
	Layout,
	Flex,
	Col,
	Button,
	Radio,
	Tag,
	Modal,
	Spin,
	Alert,
	message,
} from 'antd';
import {
	ArrowLeftOutlined,
	BarChartOutlined,
	CaretLeftOutlined,
	CaretRightOutlined,
	CheckOutlined,
	InfoCircleOutlined,
	RotateRightOutlined,
	ToolOutlined,
} from '@ant-design/icons';
import BallooningViewer from '../../../../components/BallooningViewer';
import InspectionResultsInputTable from '../../../../components/Operations/Quality/InspectionResultsInputTable';
import {
	extendedApiSlice as qualityApi,
	useGetInspectionPlanByProjectQuery,
	useGetInspectionSamplesByPlanQuery,
	useLazyGetInspectionSampleQuery,
	useGetGageTypesQuery,
} from '../../../../redux/api/quality';
import { useUpdateInspectionSampleMutation } from '../../../../redux/api/quality';
import type { appDispatch } from '../../../../redux/store';
import { InspectionSample } from '../../../../interfaces/Quality.Interface';
import { Balloon } from '../../../../interfaces/Ballooning';
import useControlsDisabled from '../../../../hooks/useControlsDisabled';
import {
	useGetUsersQuery,
	UserQueryInterface,
} from '../../../../redux/api/users';
import { UserListInterface } from '../../../../interfaces/User.interface';
import OperatorPinField from '../../../../components/Operators/OperatorPinField';
import { TABLET_BACK_BUTTON_STYLE } from '../../../../components/Operations/tabletStyles';
import { formatHandCompletion } from '../../../../utils/handCompletion';
import {
	InspectionSampleLabelingTag,
	confirmSampleLabeling,
} from '../../../../components/InspectionSampleLabeling';
import { getTotalQtyDisplay } from './lotQty';

const { Header } = Layout;
const { Text } = Typography;

interface RadioOption {
	value: string;
	label: string;
}

interface HeaderSectionProps {
	projectData: any;
	pageNumber: number;
	selectedHandGage: string;
	handGageButtonText: string;
	onBack: () => void;
	onPrevSample: () => void;
	onNextSample: () => void;
	selectedSample: InspectionSample | undefined;
	onVisualInspectionUpdate: () => void;
	onCompleteInspection: () => void;
	onOpenGageModal: () => void;
	isInModal?: boolean;
	controlsDisabled?: boolean;
	operatorId: string | number | undefined;
	users: UserListInterface[] | undefined;
	onOperatorResolved: (userId: number | undefined) => void;
	operatorError: boolean;
	onToggleLayout: () => void;
	isEmbedded?: boolean;
	operatorLocked?: boolean;
	/** Name shown instead of the PIN field when the operator comes from the URL. */
	lockedOperatorName?: string;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
	projectData,
	handGageButtonText,
	onBack,
	onPrevSample,
	onNextSample,
	selectedSample,
	onVisualInspectionUpdate,
	onCompleteInspection,
	onOpenGageModal,
	isInModal,
	controlsDisabled,
	operatorId,
	users,
	onOperatorResolved,
	operatorError,
	onToggleLayout,
	isEmbedded,
	operatorLocked,
	lockedOperatorName,
}) => {
	const visualStatus =
		selectedSample?.visual_inspection_status || 'Not Inspected';
	const sampleStatus = selectedSample?.hand_quality_status || 'In Progress';
	const isUserMissing = !operatorId;
	// PTB-6415: surface who completed the hand check and when.
	const completionInfo =
		sampleStatus === 'Completed'
			? formatHandCompletion(
					selectedSample?.hand_completed_by,
					selectedSample?.hand_completed_at
			  )
			: null;

	return (
		<Header
			style={
				isInModal
					? {
							color: '#000',
							height: 'auto',
							padding: '0 0 12px',
							width: '100%',
							position: 'relative',
							backgroundColor: 'transparent',
							borderBottom: '1px solid #f0f0f0',
					  }
					: {
							color: '#000',
							height: 'auto',
							padding: '10px 20px',
							width: '100%',
							position: 'relative',
							zIndex: 1,
							backgroundColor: '#fff',
							boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
					  }
			}
		>
			{/* Row 1: Stats (grouped in pills) + Keyence badge + Layout toggle + Back */}
			<Flex justify="space-between" align="center" style={{ lineHeight: 1.4 }}>
				<Flex gap={10} align="center" wrap="wrap" style={{ fontSize: 14 }}>
					{/* Group: Job configuration */}
					<Flex
						gap={14}
						align="center"
						style={{
							backgroundColor: '#fafafa',
							border: '1px solid #e8e8e8',
							borderLeft: '3px solid #bfbfbf',
							borderRadius: 6,
							padding: '4px 12px',
						}}
					>
						<InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
						<span>
							<Text type="secondary">Total qty:</Text>{' '}
							<Tag
								color="blue"
								style={{ margin: 0, fontWeight: 600, fontSize: 14 }}
							>
								{getTotalQtyDisplay(projectData.project?.order_line_item)}
							</Tag>
						</span>
						<span>
							<Text type="secondary">Sample Size:</Text>{' '}
							<Tag style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
								{projectData.project?.order_line_item?.required_aql_samples}
							</Tag>
						</span>
						<span>
							<Text type="secondary">Interval:</Text>{' '}
							<Tag style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>1</Tag>{' '}
							<Text type="secondary">in</Text>{' '}
							<Tag style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
								{projectData.project?.order_line_item?.aql_interval}
							</Tag>{' '}
							<Text type="secondary">Parts</Text>
						</span>
					</Flex>

					{/* Group: Inspection progress */}
					<Flex
						gap={14}
						align="center"
						style={{
							backgroundColor: '#fafafa',
							border: '1px solid #e8e8e8',
							borderLeft: '3px solid #bfbfbf',
							borderRadius: 6,
							padding: '4px 12px',
						}}
					>
						<BarChartOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
						<span>
							<Tag
								color="green"
								style={{ margin: 0, fontWeight: 600, fontSize: 14 }}
							>
								{projectData?.completed_count || 0}
							</Tag>{' '}
							<Text type="secondary">Pass</Text>
						</span>
						<span>
							<Tag
								color="red"
								style={{ margin: 0, fontWeight: 600, fontSize: 14 }}
							>
								{projectData?.failed_count || 0}
							</Tag>{' '}
							<Text type="secondary">Failed</Text>
						</span>
						<span>
							<Tag
								color="gold"
								style={{ margin: 0, fontWeight: 600, fontSize: 14 }}
							>
								{projectData?.in_progress_count || 0}
							</Tag>{' '}
							<Text type="secondary">In Progress</Text>
						</span>
						<span>
							<Tag style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
								{(projectData?.samples?.length || 0) -
									(projectData?.completed_count || 0) -
									(projectData?.failed_count || 0) -
									(projectData?.in_progress_count || 0)}
							</Tag>{' '}
							<Text type="secondary">Not started</Text>
						</span>
					</Flex>

					{projectData?.has_keyence_measurements && (
						<Tag
							color="blue"
							style={{
								fontSize: 14,
								padding: '4px 12px',
								margin: 0,
								fontWeight: 500,
							}}
						>
							Requires Keyence
						</Tag>
					)}

					{projectData?.requires_inspection_sample_labeling && (
						<InspectionSampleLabelingTag
							style={{
								fontSize: 14,
								padding: '4px 12px',
								margin: 0,
								fontWeight: 500,
							}}
						/>
					)}
				</Flex>
				<Flex gap={8} align="center">
					{completionInfo && (
						<Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
							{completionInfo}
						</Text>
					)}
					<Button
						type="text"
						size="large"
						icon={<RotateRightOutlined />}
						onClick={onToggleLayout}
						title="Toggle layout"
					/>
					{!isInModal && !isEmbedded && (
						<Button
							type="primary"
							size="large"
							style={TABLET_BACK_BUTTON_STYLE}
							onClick={onBack}
						>
							<ArrowLeftOutlined />
							<span>Back</span>
						</Button>
					)}
				</Flex>
			</Flex>

			{/* Row 2: Controls + Sample navigation */}
			<Flex
				justify="space-between"
				align="center"
				style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f5f5f5' }}
			>
				<Flex gap={12} align="center" wrap="wrap">
					{/* Group: Inspection actions */}
					<Flex
						gap={12}
						align="center"
						style={{
							backgroundColor: '#fafafa',
							border: '1px solid #e8e8e8',
							borderLeft: '3px solid #bfbfbf',
							borderRadius: 6,
							padding: '6px 12px',
						}}
					>
						<ToolOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
						<Flex gap={8} align="center">
							<Text strong style={{ fontSize: 15 }}>
								Hand:
							</Text>
							<Button
								size="large"
								style={{
									backgroundColor: 'white',
									borderColor: 'red',
									color: 'red',
									fontWeight: 500,
								}}
								onClick={onOpenGageModal}
								disabled={controlsDisabled}
							>
								{handGageButtonText}
							</Button>
						</Flex>
						<div style={{ width: 1, height: 32, backgroundColor: '#e8e8e8' }} />
						<Flex gap={8} align="center">
							<Text strong style={{ fontSize: 15 }}>
								Visual:
							</Text>
							<Button
								type="primary"
								size="large"
								style={{
									backgroundColor: visualStatus === 'Passed' ? 'green' : 'gray',
									borderColor: visualStatus === 'Passed' ? 'green' : 'gray',
									color: 'white',
									fontWeight: 500,
								}}
								onClick={onVisualInspectionUpdate}
								disabled={
									controlsDisabled || visualStatus === 'Passed' || isUserMissing
								}
							>
								{visualStatus}
							</Button>
						</Flex>
						<div style={{ width: 1, height: 32, backgroundColor: '#e8e8e8' }} />
						{operatorLocked ? (
							<Flex gap={8} align="center">
								<Text strong style={{ fontSize: 15 }}>
									Operator:
								</Text>
								<Text style={{ fontSize: 15 }}>{lockedOperatorName}</Text>
							</Flex>
						) : (
							<OperatorPinField
								variant="inline"
								userData={users || []}
								expectedGroups={['Machine Operators']}
								value={operatorId}
								disabled={controlsDisabled}
								showError={operatorError}
								onResolved={onOperatorResolved}
							/>
						)}
					</Flex>

					{/* Primary action: Complete inspection */}
					<Button
						type="primary"
						icon={sampleStatus === 'Completed' && <CheckOutlined />}
						size="large"
						disabled={
							controlsDisabled || visualStatus !== 'Passed' || isUserMissing
						}
						onClick={onCompleteInspection}
						style={{
							backgroundColor: sampleStatus === 'Completed' ? 'green' : 'gray',
							borderColor: sampleStatus === 'Completed' ? 'green' : 'gray',
							color: 'white',
							fontWeight: 500,
						}}
					>
						{sampleStatus}
					</Button>
					{
						/* Keyence tag moved to header top row */
						false && (
							<Col>
								<Tag color="blue">Keyence</Tag>
							</Col>
						)
					}
				</Flex>
				<Flex gap={12} align="center">
					<Button
						shape="circle"
						size="large"
						type="primary"
						icon={<CaretLeftOutlined />}
						onClick={onPrevSample}
					/>
					<Button
						shape="circle"
						size="large"
						type="primary"
						style={{ fontWeight: 600, fontSize: 18 }}
					>
						{selectedSample?.sample_number}
					</Button>
					<Button
						shape="circle"
						size="large"
						type="primary"
						icon={<CaretRightOutlined />}
						onClick={onNextSample}
					/>
				</Flex>
			</Flex>
		</Header>
	);
};

interface InspectionResultsEntryPageProps {
	projectId?: string;
	sampleId?: string;
	isInModal?: boolean;
}

const InspectionResultsEntryPage: React.FC<InspectionResultsEntryPageProps> = ({
	projectId: projectIdProp,
	sampleId: sampleIdProp,
	isInModal = false,
}) => {
	const { id: urlProjectId, sampleId: urlSampleId } = useParams<{
		id: string;
		sampleId?: string;
	}>();

	const projectId = projectIdProp || urlProjectId;
	const sampleId = sampleIdProp || urlSampleId;

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isEmbedded = searchParams.get('embedded') === 'true';
	const operatorParam = searchParams.get('operator');

	// samples=ids: this page only counts the plan's samples here; their results come
	// from the by-plan query below (PTB-7009).
	const {
		data: projectData,
		isLoading: projectLoading,
		error: projectError,
	} = useGetInspectionPlanByProjectQuery({ projectId: projectId!, samples: 'ids' });
	const [planId, setPlanId] = useState<string | undefined>(undefined);

	const {
		data: samples,
		isLoading: samplesLoading,
		error: samplesError,
	} = useGetInspectionSamplesByPlanQuery(
		{ planId: planId! },
		{ skip: !planId, refetchOnMountOrArgChange: true }
	);

	// PTB-7009: refresh only the sample being worked on. Re-fetching the whole plan
	// (every sample with every result, ~950 KB on a 125-sample lot) on each
	// Prev/Next/Complete kept the server busy for 4-8s per click. The fresh copy also
	// replaces the plan list's entry, so navigating back shows what was entered.
	const dispatch = useDispatch<appDispatch>();
	const [fetchSample] = useLazyGetInspectionSampleQuery();
	const refreshSample = useCallback(
		async (id: string): Promise<InspectionSample | undefined> => {
			const { data } = await fetchSample(id);
			if (data && planId) {
				dispatch(
					qualityApi.util.updateQueryData(
						'getInspectionSamplesByPlan',
						{ planId },
						(draft) => {
							const index = draft.findIndex((s) => s.id === id);
							if (index !== -1) draft[index] = data;
						}
					)
				);
			}
			return data;
		},
		[fetchSample, dispatch, planId]
	);

	const requiresSampleLabeling = Boolean(
		projectData?.requires_inspection_sample_labeling
	);

	const { data: gageTypesData } = useGetGageTypesQuery({});

	const userLayoutPreference = localStorage.getItem(
		'inspectionResultsEntryLayout'
	);
	const [layout, setLayout] = useState<'vertical' | 'horizontal'>(
		userLayoutPreference === 'vertical' || userLayoutPreference === 'horizontal'
			? userLayoutPreference
			: 'horizontal'
	);

	const handleToggleLayout = useCallback(() => {
		setLayout((prev) => {
			const next = prev === 'horizontal' ? 'vertical' : 'horizontal';
			localStorage.setItem('inspectionResultsEntryLayout', next);
			return next;
		});
	}, []);

	const [isOperatorView, setIsOperatorView] = useState(true);
	const [isReadyToComplete, setIsReadyToComplete] = useState(false);
	const [activeBalloonId, setActiveBalloonId] = useState<string | null>(null);
	const [centerOnActive, setCenterOnActive] = useState(false);

	const [selectedSample, setSelectedSample] = useState<
		InspectionSample | undefined
	>(undefined);
	const initializedRef = useRef(false);

	const [operatorId, setOperatorId] = useState<string | number | undefined>(
		undefined
	);
	const [lockedOperatorName, setLockedOperatorName] = useState<
		string | undefined
	>(undefined);
	const [operatorError, setOperatorError] = useState(false);

	const userQuery: UserQueryInterface = useMemo(
		() => ({
			groups__name: 'Machine Operators',
		}),
		[]
	);

	const { data: usersData } = useGetUsersQuery(userQuery);
	const users = usersData?.results;

	const operatorLockedRef = useRef(false);
	useEffect(() => {
		if (!operatorParam || !users?.length || operatorLockedRef.current) return;
		const matched = users.find((u) => u.email === operatorParam);
		if (matched) {
			setOperatorId(matched.id);
			setLockedOperatorName(matched.full_name || matched.email);
			operatorLockedRef.current = true;
		}
	}, [users, operatorParam]);
	const operatorLocked = operatorLockedRef.current && !!operatorId;

	useEffect(() => {
		if (samples && samples.length > 0 && !initializedRef.current) {
			if (sampleId) {
				const matchedSample = samples.find((s) => s.id === sampleId);
				setSelectedSample(matchedSample || samples[0]);
			} else {
				const incompleteSamples = samples.filter(
					(s) => s.hand_quality_status !== 'Completed'
				);
				const nextSample =
					incompleteSamples.sort(
						(a, b) => a.sample_number - b.sample_number
					)[0] ||
					samples.find((s) => s.sample_number === 1) ||
					samples[0];
				setSelectedSample(nextSample);
			}
			initializedRef.current = true;
		}
	}, [samples, sampleId]);

	useEffect(() => {
		if (projectData) {
			setPlanId(projectData.id);
		}
	}, [projectData]);

	const [localBalloons, setLocalBalloons] = useState<Balloon[]>([]);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pageID, setPageID] = useState<string | null>(null);
	const [balloonSize, setBalloonSize] = useState<number>(50);

	const [selectedHandGage, setSelectedHandGage] = useState('all');
	const [handGageOptions, setHandGageOptions] = useState<RadioOption[]>([
		{ value: 'all', label: 'All' },
	]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [handGageButtonText, setHandGageButtonText] = useState('All');

	const renderOptions = (options: RadioOption[], color?: string) => {
		const updatedOptions = [...options, { value: 'all', label: 'All' }];
		return updatedOptions.map((opt) => (
			<Radio.Button
				onClick={() => setIsModalVisible(false)}
				style={{ color, borderColor: color }}
				key={opt.value}
				value={opt.value}
			>
				{opt.label}
			</Radio.Button>
		));
	};

	const renderHandGageOptions = (disabled?: boolean) => (
		<div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
			<Radio.Group
				defaultValue={selectedHandGage}
				buttonStyle="solid"
				size="large"
				onChange={(e) => setSelectedHandGage(e.target.value)}
				value={selectedHandGage}
				disabled={disabled}
			>
				{renderOptions(handGageOptions, '')}
			</Radio.Group>
		</div>
	);

	// Set balloons and balloon size when project data loads (NOT on every selectedSample change)
	useEffect(() => {
		if (projectData) {
			setBalloonSize(projectData.project.balloon_size);
		}
		if (projectData && projectData.project.ballooning_pages) {
			setLocalBalloons(
				projectData.project.ballooning_pages
					.flatMap((page: any) => page.balloons || [])
					.filter((b: Balloon) => b.measurement_strategy !== 'Keyence')
			);
		}
	}, [projectData]);

	// Update page ID and gage options when page or gage data changes
	useEffect(() => {
		if (projectData && projectData.project.ballooning_pages) {
			const selectedPage = projectData.project.ballooning_pages.find(
				(page: any) => page.page_number === pageNumber
			);
			if (selectedPage && gageTypesData) {
				setPageID(selectedPage.id);
				const gageTypes = selectedPage.balloons
					? selectedPage.balloons.reduce((acc: Set<string>, b: Balloon) => {
							if (b.gage_type) acc.add(b.gage_type);
							return acc;
					  }, new Set())
					: new Set();
				const gageTypeOptions: RadioOption[] = Array.from(gageTypes).map(
					(value) => ({
						value,
						label:
							gageTypesData.find((g: any) => g.id === value)?.name ||
							`Unknown (${value})`,
					})
				);
				setHandGageOptions(gageTypeOptions);
				setHandGageButtonText(
					selectedHandGage === 'all'
						? 'All'
						: gageTypeOptions.find((g) => g.value === selectedHandGage)
								?.label || ''
				);
			}
		}
	}, [projectData, pageNumber, selectedHandGage, gageTypesData]);

	useEffect(() => {
		if (
			selectedSample &&
			selectedSample.hand_quality_status === 'In Progress'
		) {
			setIsReadyToComplete(true);
		}
	}, [selectedSample]);

	// Fetch the target sample before selecting it: the results table loads its inputs
	// once per sample id, so it must receive the fresh copy, not the cached one. Only
	// the latest click applies if the operator clicks again while one is in flight.
	const navigationRef = useRef(0);
	const goToSample = useCallback(
		async (sampleNumber: number) => {
			const target = samples?.find((s) => s.sample_number === sampleNumber);
			if (!target) return;
			const request = ++navigationRef.current;
			const fresh = await refreshSample(target.id);
			if (request === navigationRef.current) {
				setSelectedSample(fresh || target);
			}
		},
		[samples, refreshSample]
	);

	const handlePrevSample = useCallback(() => {
		if (selectedSample) {
			goToSample(selectedSample.sample_number - 1);
		}
	}, [selectedSample, goToSample]);

	const handleNextSample = useCallback(() => {
		if (selectedSample) {
			goToSample(selectedSample.sample_number + 1);
		}
	}, [selectedSample, goToSample]);

	const [updateInspectionSample] = useUpdateInspectionSampleMutation();

	const handleVisualInspectionUpdate = useCallback(() => {
		if (!operatorId) {
			setOperatorError(true);
			message.error(
				'Please enter your PIN before performing visual inspection.'
			);
			return;
		} else {
			setOperatorError(false);
		}

		if (
			selectedSample &&
			selectedSample.visual_inspection_status !== 'Passed'
		) {
			const updatedSample = {
				...selectedSample,
				visual_inspection_status: 'Passed',
			};
			updateInspectionSample({
				id: selectedSample.id,
				values: { visual_inspection_status: 'Passed' },
			});
			setSelectedSample(updatedSample);
		}
	}, [selectedSample, operatorId, updateInspectionSample]);

	const getMissingResults = (sample) => {
		if (!sample || !Array.isArray(sample.from_sample)) {
			return [];
		}

		return sample.from_sample.filter(
			(item) =>
				item.strategy !== 'Keyence' &&
				item.value === '0.000000' &&
				item.passed !== true
		);
	};

	const handleCompleteInspection = useCallback(async () => {
		if (!selectedSample || !selectedSample.id) return;

		if (!operatorId) {
			setOperatorError(true);
			message.error('Please enter your PIN before completing inspection.');
			return;
		} else {
			setOperatorError(false);
		}

		const freshSample = await refreshSample(selectedSample.id);

		if (!freshSample) {
			message.error('Failed to fetch latest sample data.');
			return;
		}

		const missingResults = getMissingResults(freshSample);
		if (
			freshSample.visual_inspection_status === 'Passed' &&
			freshSample.hand_quality_status !== 'Completed'
		) {
			if (missingResults.length > 0) {
				message.warning(
					'Some results are missing. Please complete all required inspections.'
				);
				return;
			}

			// Hard requirement, so it gates the completion rather than warning
			// about it: an unlabeled sample cannot be traced back to the part it
			// came from (PTB-6619).
			if (requiresSampleLabeling) {
				const labeled = await confirmSampleLabeling(freshSample.sample_number);
				if (!labeled) {
					message.warning(
						'Bag and label the sample, then complete the inspection.'
					);
					return;
				}
			}

			try {
				await updateInspectionSample({
					id: freshSample.id,
					values: {
						hand_quality_status: 'Completed',
						operator_id: operatorId,
					},
				}).unwrap();
			} catch (err: any) {
				let detail: string | null = null;
				const data = err?.data;

				// If backend returned structured JSON
				if (data && typeof data === 'object') {
					const qualityMsg =
						(Array.isArray((data as any).quality_status) &&
							(data as any).quality_status[0]) ||
						null;
					detail =
						(data as any).message || (data as any).detail || qualityMsg || null;
				}

				// If we hit a parsing error and only have raw HTML/text, try to extract the key message
				if (
					!detail &&
					err?.status === 'PARSING_ERROR' &&
					typeof err?.data === 'string'
				) {
					const raw = err.data as string;
					const match = raw.match(
						/Machining must be Complete before completing Quality Status\./
					);
					if (match) {
						detail = match[0];
					}
				}

				// Fallbacks
				if (!detail) {
					// Avoid surfacing the low-level SyntaxError text to the user
					detail = 'Failed to complete hand quality inspection.';
				}

				message.error(detail);
				return;
			}

			const updatedSample = await refreshSample(freshSample.id);
			setSelectedSample(updatedSample);

			message.success('Sample hand quality status marked as completed.');
		} else if (freshSample.hand_quality_status === 'Completed') {
			try {
				await updateInspectionSample({
					id: freshSample.id,
					values: {
						hand_quality_status: 'In Progress',
						operator_id: operatorId,
					},
				}).unwrap();
			} catch (err: any) {
				let detail: string | null = null;
				const data = err?.data;

				if (data && typeof data === 'object') {
					detail = (data as any).message || (data as any).detail || null;
				}

				if (
					!detail &&
					err?.status === 'PARSING_ERROR' &&
					typeof err?.data === 'string'
				) {
					detail = 'Failed to update hand quality status.';
				}

				if (!detail) {
					detail =
						err?.error ||
						err?.message ||
						'Failed to update hand quality status.';
				}

				message.error(detail);
				return;
			}

			const updatedSample = await refreshSample(freshSample.id);
			setSelectedSample(updatedSample);

			message.success('Sample hand quality status marked as in progress.');
		}
	}, [
		selectedSample?.id,
		operatorId,
		refreshSample,
		updateInspectionSample,
		requiresSampleLabeling,
	]);

	const handleOpenGageModal = useCallback(() => {
		setIsModalVisible(true);
	}, []);

	const filteredBalloons = useMemo(() => {
		return selectedHandGage === 'all'
			? localBalloons
			: localBalloons.filter((b: Balloon) => b.gage_type === selectedHandGage);
	}, [localBalloons, selectedHandGage]);

	// Disable inputs based on the status of the BATCH being inspected, not the shared
	// plan's owner OLI (project.order_line_item is the lot's first batch, so keying off
	// it locked every batch once the first was signed off Completed). PTB-7023.
	const lineItemStatus =
		selectedSample?.batch_quality_status ?? projectData?.project?.order_line_item?.status;
	const { controlsDisabled } = useControlsDisabled(lineItemStatus);

	if (projectLoading || samplesLoading) return <Spin size="large" />;
	if (projectError || samplesError)
		return <Alert type="error" message="Failed to load data" />;

	return (
		<div
			style={{
				overflow: 'hidden',
				WebkitTouchCallout: 'none',
				WebkitUserSelect: 'none',
				userSelect: 'none',
				// Fill the screen (or the modal body) exactly so the panels scroll, never the page.
				height: isInModal ? '100%' : '100vh',
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
			onContextMenu={(e) => e.preventDefault()}
		>
			<Flex gap="middle" style={{ flex: 1, minHeight: 0 }}>
				<Layout
					style={{
						color: '#000',
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						width: '100%',
						...(isInModal ? { background: 'transparent' } : { borderRadius: 8, overflow: 'hidden' }),
					}}
				>
					<HeaderSection
						projectData={projectData}
						pageNumber={pageNumber}
						selectedHandGage={selectedHandGage}
						handGageButtonText={handGageButtonText}
						onBack={() => navigate('/tablet/operations/qc/machine')}
						onPrevSample={handlePrevSample}
						onNextSample={handleNextSample}
						selectedSample={selectedSample}
						onVisualInspectionUpdate={handleVisualInspectionUpdate}
						onCompleteInspection={handleCompleteInspection}
						onOpenGageModal={handleOpenGageModal}
						isInModal={isInModal}
						controlsDisabled={controlsDisabled}
						operatorId={operatorId}
						users={users}
						onOperatorResolved={(userId) => {
							setOperatorId(userId);
							if (userId) {
								setOperatorError(false);
							}
						}}
						operatorError={operatorError}
						onToggleLayout={handleToggleLayout}
						isEmbedded={isEmbedded}
						operatorLocked={operatorLocked}
						lockedOperatorName={lockedOperatorName}
					/>
					<Layout.Content
						style={
							isInModal
								? {
										textAlign: 'center',
										lineHeight: '110px',
										backgroundColor: 'transparent',
										flex: 1,
										minHeight: 0,
										width: '100%',
										paddingTop: 12,
										overflowY: 'hidden',
								  }
								: {
										textAlign: 'center',
										lineHeight: '110px',
										backgroundColor: '#fff',
										flex: 1,
										minHeight: 0,
										width: '100%',
										overflowY: 'hidden',
								  }
						}
					>
						<div style={{ height: '100%' }}>
							<Splitter className="gap-1 w-full" layout={layout} key={layout}>
								<Splitter.Panel
									className="flex flex-col gap-1 h-full"
									style={{ zIndex: '20' }}
									resizable={false}
									min={100}
								>
									<BallooningViewer
										activeBalloonId={activeBalloonId}
										setActiveBalloonId={setActiveBalloonId}
										setCenterOnActive={setCenterOnActive}
										centerOnActive={centerOnActive}
										localBalloons={filteredBalloons}
										setLocalBalloons={setLocalBalloons}
										balloonSize={balloonSize}
										editable={false}
										pageID={pageID}
										pageNumber={pageNumber}
										setPageNumber={setPageNumber}
										isTabletView={true}
										projectId={projectId}
										showLeaderLines
									/>
								</Splitter.Panel>
								<Splitter.Panel
									className={isInModal ? 'bg-white gap-1 h-full' : 'border bg-white gap-1 h-full'}
									resizable={false}
									style={{
										flex: '1',
										overflowY: 'auto',
										padding: isInModal ? '0 0 0 16px' : '40px',
										overflowX: 'hidden',
									}}
								>
									<InspectionResultsInputTable
										balloons={filteredBalloons}
										isOperatorView={isOperatorView}
										currentSample={selectedSample}
										unit={projectData.project?.unit}
										missingResults={getMissingResults(selectedSample)}
										controlsDisabled={controlsDisabled}
										onBalloonClick={(balloonId: string) => {
											setActiveBalloonId(balloonId);
											setCenterOnActive(true);
										}}
									/>
								</Splitter.Panel>
							</Splitter>
						</div>
					</Layout.Content>
				</Layout>
			</Flex>
			<Modal
				title="Gage Types"
				open={isModalVisible}
				onOk={() => setIsModalVisible(false)}
				onCancel={() => setIsModalVisible(false)}
				width="380px"
			>
				{renderHandGageOptions(controlsDisabled)}
			</Modal>
		</div>
	);
};

export default InspectionResultsEntryPage;
